export interface TrendPoint {
  date: string;
  tokens: number;
  costUsd: number;
}

/** `YYYY-MM-DD` (the server's UTC day) as a local-midnight Date, so the chart never shifts a day. */
export function dayToDate(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export const formatDay = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/** 1,234 -> "1.2K", 3,400,000 -> "3.4M". For axis ticks and summaries. */
export function formatCompact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${+(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${+(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${+(n / 1e3).toFixed(1)}K`;
  return String(Math.round(n));
}

/** Exact cost: a single request can cost fractions of a cent, so 2 decimals would show "$0.00". */
export const formatUsd = (n: number) => `$${n.toFixed(n > 0 && n < 0.01 ? 6 : 2)}`;

/** Short cost for axis ticks: "$0", "$0.0021", "$1.50". */
export function formatUsdAxis(n: number): string {
  if (n === 0) return '$0';
  if (Math.abs(n) >= 1) return `$${n.toFixed(2)}`;
  return `$${+n.toPrecision(2)}`;
}

export interface TrendSummary {
  totalTokens: number;
  totalCostUsd: number;
  activeDays: number;
  maxTokens: number;
  maxCostUsd: number;
}

export function summarizeTrend(history: readonly TrendPoint[]): TrendSummary {
  return {
    totalTokens: history.reduce((n, p) => n + p.tokens, 0),
    totalCostUsd: history.reduce((n, p) => n + p.costUsd, 0),
    activeDays: history.filter((p) => p.tokens > 0 || p.costUsd > 0).length,
    maxTokens: history.reduce((n, p) => Math.max(n, p.tokens), 0),
    maxCostUsd: history.reduce((n, p) => Math.max(n, p.costUsd), 0),
  };
}

/**
 * A y-axis that starts at zero and always has height. Left to auto-scale, a
 * series that is all zeros (or one spike) gets a degenerate or negative axis —
 * the "wonky" trend chart.
 */
export function yDomainFor(max: number): [number, number] {
  return [0, max > 0 ? max * 1.1 : 1];
}
